import Link from "next/link";

import { getIconForTitle } from "@/features/marketing-content/utils/icon-map";

import type { SwitchingSteps } from "@/content/alternatives/schema";

interface SwitchingTimelineProps extends SwitchingSteps {
  competitorName: string;
}

export function SwitchingTimeline({
  heading,
  description,
  note,
  videoUrl,
  steps,
  competitorName,
}: SwitchingTimelineProps) {
  const headingParts = heading.split("Rebolt");

  return (
    <section className="bg-ash-100 py-16 md:py-24">
      <div className="mx-auto max-w-6xl px-6">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          <div>
            <h2 className="font-clash text-3xl font-medium leading-tight tracking-snug text-ash-900 md:text-5xl">
              {headingParts.length > 1 ? (
                <>
                  {headingParts[0]}
                  <span className="text-rebolt-500">Rebolt</span>
                  {headingParts[1]}
                </>
              ) : (
                heading
              )}
            </h2>

            <div className="mt-6">
              <Link
                href="https://cal.com/team/builtright/launch"
                className="inline-flex items-center rounded-full bg-rebolt-500 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-rebolt-600"
              >
                Get More Business
              </Link>
            </div>

            {videoUrl && (
              <div className="mt-6 overflow-hidden rounded-2xl">
                <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
                  <iframe
                    src={videoUrl}
                    title={`Rebolt Demo - Switching from ${competitorName}`}
                    className="absolute inset-0 size-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {note && <p className="mt-3 text-sm font-medium text-ash-500">{note}</p>}
          </div>

          <div>
            <p className="mb-6 text-lg font-medium text-ash-700">{description}</p>

            <div className="relative">
              <div className="absolute left-[15px] top-0 bottom-0 w-px bg-ash-300" />

              <div className="space-y-8">
                {steps.map((step, i) => {
                  const Icon = getIconForTitle(step.title);
                  return (
                    <div key={step.title} className="relative flex gap-5 pl-0">
                      <div className="relative z-10 flex size-[30px] shrink-0 items-center justify-center rounded-full bg-rebolt-500">
                        <Icon className="size-3.5 text-white" strokeWidth={2.5} />
                      </div>
                      <div className={i < steps.length - 1 ? "pb-0" : ""}>
                        <h3 className="font-clash text-lg font-medium text-ash-900">{step.title}</h3>
                        <p className="mt-1 text-sm leading-relaxed text-ash-500">{step.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
